import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Rate, Tag, Button, Modal, Input, message, Spin, Empty, Tabs
} from 'antd';
import { CommentOutlined, ShopOutlined, CalendarOutlined } from '@ant-design/icons';
import BackButton from '../../../components/BackButton';
import api from '../../../services/api';
import './PartnerReviewsPage.scss';

const { TextArea } = Input;
const { TabPane } = Tabs;

const PartnerReviewsPage = observer(() => {
  const { t } = useTranslation();
  const [productReviews, setProductReviews] = useState([]);
  const [serviceReviews, setServiceReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseModal, setResponseModal] = useState({ visible: false, review: null, type: null });
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const [productRes, serviceRes] = await Promise.all([
        api.get('/products/partner/reviews/'),
        api.get('/services/partner/reviews/')
      ]);
      setProductReviews(productRes.data || []);
      setServiceReviews(serviceRes.data || []);
    } catch (error) {
      message.error(t('partner.reviews.loadFailed'));
    }
    setLoading(false);
  };

  const handleOpenResponse = (review, type) => {
    setResponseModal({ visible: true, review, type });
    setResponseText(review.partner_response || '');
  };

  const handleSubmitResponse = async () => {
    if (!responseText.trim()) {
      message.error(t('partner.reviews.responseRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = responseModal.type === 'product'
        ? `/products/partner/reviews/${responseModal.review.id}/respond/`
        : `/services/partner/reviews/${responseModal.review.id}/respond/`;

      await api.post(endpoint, { response: responseText });
      message.success(t('partner.reviews.responseSaved'));
      setResponseModal({ visible: false, review: null, type: null });
      loadReviews();
    } catch (error) {
      message.error(error.response?.data?.error || t('partner.reviews.responseFailed'));
    }
    setSubmitting(false);
  };

  const columns = (type) => [
    {
      title: type === 'product' ? t('partner.reviews.product') : t('partner.reviews.service'),
      dataIndex: type === 'product' ? 'product_title' : 'service_name',
      key: 'item',
      render: (text, record) => (
        <div>
          <strong>{text || record.product_name}</strong>
        </div>
      ),
    },
    {
      title: t('partner.reviews.customer'),
      dataIndex: 'customer_name',
      key: 'customer',
    },
    {
      title: t('partner.reviews.rating'),
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      render: (rating) => <Rate disabled defaultValue={rating} />,
    },
    {
      title: t('partner.reviews.review'),
      dataIndex: 'comment',
      key: 'comment',
      render: (comment) => comment || <em style={{ color: '#999' }}>{t('partner.reviews.noComment')}</em>,
    },
    {
      title: t('common.date'),
      dataIndex: 'created_at',
      key: 'date',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('partner.reviews.response'),
      key: 'response',
      width: 150,
      render: (_, record) => (
        <div>
          {record.partner_response ? (
            <Tag color="green">{t('partner.reviews.responded')}</Tag>
          ) : (
            <Tag color="orange">{t('partner.reviews.noResponse')}</Tag>
          )}
          <Button
            type="link"
            size="small"
            icon={<CommentOutlined />}
            onClick={() => handleOpenResponse(record, type)}
          >
            {record.partner_response ? t('common.edit') : t('partner.reviews.respond')}
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/partner/dashboard" label={t('partner.backToDashboard')} />

      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
            <CommentOutlined /> {t('partner.reviews.title')}
          </span>
        }
      >
        <Tabs defaultActiveKey="products">
          <TabPane
            tab={
              <span>
                <ShopOutlined /> {t('partner.reviews.productReviews')} ({productReviews.length})
              </span>
            }
            key="products"
          >
            {productReviews.length === 0 ? (
              <Empty description={t('partner.reviews.noProductReviews')} />
            ) : (
              <Table
                columns={columns('product')}
                dataSource={productReviews}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                <CalendarOutlined /> {t('partner.reviews.serviceReviews')} ({serviceReviews.length})
              </span>
            }
            key="services"
          >
            {serviceReviews.length === 0 ? (
              <Empty description={t('partner.reviews.noServiceReviews')} />
            ) : (
              <Table
                columns={columns('service')}
                dataSource={serviceReviews}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Response Modal */}
      <Modal
        title={t('partner.reviews.respondToReview')}
        open={responseModal.visible}
        onCancel={() => setResponseModal({ visible: false, review: null, type: null })}
        onOk={handleSubmitResponse}
        confirmLoading={submitting}
        okText={t('partner.reviews.saveResponse')}
        cancelText={t('common.cancel')}
      >
        {responseModal.review && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>{t('partner.reviews.customer')}:</strong> {responseModal.review.customer_name}</p>
            <p><strong>{t('partner.reviews.rating')}:</strong> <Rate disabled defaultValue={responseModal.review.rating} /></p>
            <p><strong>{t('partner.reviews.review')}:</strong> {responseModal.review.comment || t('partner.reviews.noComment')}</p>
          </div>
        )}
        <TextArea
          rows={4}
          placeholder={t('partner.reviews.writeResponse')}
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
        />
      </Modal>
    </div>
  );
});

export default PartnerReviewsPage;
